// Share dialog Drive-style — 2 tabs: Usuarios (autocomplete) + Link público.
// Tokens semánticos del theme para respetar dark/light mode.

import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, Globe, Link2Off, Search, Trash2, Users, X } from 'lucide-react';

import {
  createPublicLink,
  createShare,
  listOrgUsers,
  listShares,
  revokePublicLink,
  revokeShare,
} from '../api';
import type {
  DocumentOut,
  OrgUserOut,
  PublicLinkOut,
  SharePermission,
  ShareOut,
} from '../types';

interface Props {
  doc: DocumentOut;
  onClose: () => void;
  onShared?: () => void;
}

type TabKey = 'users' | 'public';

export function ShareDialog({ doc, onClose, onShared }: Props) {
  const [tab, setTab] = useState<TabKey>('users');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-xl rounded-lg border border-border bg-card text-foreground shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground">Compartir</div>
            <h3 className="truncate font-semibold">{doc.title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex border-b border-border">
          <TabButton active={tab === 'users'} onClick={() => setTab('users')} icon={<Users size={14} />}>
            Personas de la org
          </TabButton>
          <TabButton active={tab === 'public'} onClick={() => setTab('public')} icon={<Globe size={14} />}>
            Link público
          </TabButton>
        </div>

        <div className="p-4">
          {tab === 'users' ? (
            <UsersTab doc={doc} onShared={onShared} />
          ) : (
            <PublicLinkTab doc={doc} onChanged={onShared} />
          )}
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition ${
        active
          ? 'border-b-2 border-primary text-foreground'
          : 'border-b-2 border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

// ── Tab Usuarios ───────────────────────────────────────────

function UsersTab({ doc, onShared }: { doc: DocumentOut; onShared?: () => void }) {
  const [shares, setShares] = useState<ShareOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<OrgUserOut[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [permission, setPermission] = useState<SharePermission>('view');
  const [expiresDays, setExpiresDays] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listShares(doc.id)
      .then(setShares)
      .catch((e) => setError(String(e?.message || e)))
      .finally(() => setLoading(false));
  }, [doc.id]);

  // Debounced autocomplete
  useEffect(() => {
    const t = setTimeout(() => {
      setUserSearchLoading(true);
      listOrgUsers(query.trim() || undefined)
        .then(setUsers)
        .catch(() => setUsers([]))
        .finally(() => setUserSearchLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const alreadySharedIds = useMemo(
    () => new Set(shares.map((s) => s.shared_with_user_id)),
    [shares],
  );

  async function shareWith(user: OrgUserOut) {
    setError(null);
    try {
      const expires_at = expiresDays
        ? new Date(Date.now() + expiresDays * 86400 * 1000).toISOString()
        : null;
      const created = await createShare(doc.id, {
        shared_with_user_id: user.id,
        permission,
        expires_at,
      });
      setShares((prev) => [created, ...prev]);
      onShared?.();
    } catch (e: any) {
      setError(String(e?.message || e));
    }
  }

  async function handleRevoke(shareId: string) {
    if (!confirm('¿Revocar acceso de este usuario?')) return;
    try {
      await revokeShare(doc.id, shareId);
      setShares((prev) => prev.filter((s) => s.id !== shareId));
      onShared?.();
    } catch (e: any) {
      alert(`Error: ${e?.message || e}`);
    }
  }

  return (
    <div className="space-y-4">
      {/* Buscador autocompletado */}
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar persona por nombre o email…"
          className="w-full rounded border border-input bg-background py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
        />
      </div>

      {/* Permission + expires controls compactos */}
      <div className="grid grid-cols-2 gap-2">
        <select
          value={permission}
          onChange={(e) => setPermission(e.target.value as SharePermission)}
          className="rounded border border-input bg-background px-2 py-1.5 text-xs text-foreground"
        >
          <option value="view">Puede ver / descargar</option>
          <option value="comment">Puede comentar</option>
          <option value="edit">Puede editar</option>
        </select>
        <select
          value={expiresDays ?? ''}
          onChange={(e) => setExpiresDays(e.target.value ? Number(e.target.value) : null)}
          className="rounded border border-input bg-background px-2 py-1.5 text-xs text-foreground"
        >
          <option value="">Acceso indefinido</option>
          <option value="1">Caduca en 1 día</option>
          <option value="7">Caduca en 7 días</option>
          <option value="30">Caduca en 30 días</option>
          <option value="90">Caduca en 90 días</option>
        </select>
      </div>

      {error && (
        <div className="rounded border border-destructive/30 bg-destructive/10 px-2 py-1 text-xs text-destructive">
          {error}
        </div>
      )}

      {/* Resultados del autocomplete */}
      <div className="max-h-48 overflow-y-auto rounded border border-border">
        {userSearchLoading ? (
          <div className="p-3 text-center text-xs text-muted-foreground">Buscando…</div>
        ) : users.length === 0 ? (
          <div className="p-3 text-center text-xs text-muted-foreground">
            {query ? 'Sin coincidencias' : 'No hay usuarios disponibles'}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {users.map((u) => {
              const isShared = alreadySharedIds.has(u.id);
              return (
                <li key={u.id} className="flex items-center justify-between px-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {`${u.first_name} ${u.last_name}`.trim() || u.username}
                    </div>
                    {u.email && (
                      <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                    )}
                  </div>
                  {isShared ? (
                    <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
                      <Check size={12} /> Compartido
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => shareWith(u)}
                      className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                    >
                      Compartir
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Lista de quien ya tiene acceso */}
      <div>
        <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Personas con acceso
        </h4>
        {loading ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : shares.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aún no se ha compartido con nadie.</p>
        ) : (
          <ul className="divide-y divide-border rounded border border-border">
            {shares.map((s) => (
              <li key={s.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <div className="min-w-0">
                  <span className="font-medium text-foreground">
                    {s.shared_with_user_email ?? `User #${s.shared_with_user_id}`}
                  </span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {s.permission}
                    {s.expires_at &&
                      ` · caduca ${new Date(s.expires_at).toLocaleDateString()}`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRevoke(s.id)}
                  className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  title="Revocar"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Tab Link público ───────────────────────────────────────

function PublicLinkTab({ doc, onChanged }: { doc: DocumentOut; onChanged?: () => void }) {
  const [link, setLink] = useState<PublicLinkOut | null>(
    doc.visibility === 'public' && doc.public_token
      ? {
          public_token: doc.public_token,
          public_url: '',
          expires_at: doc.expires_at,
        }
      : null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleCreate() {
    setBusy(true);
    setError(null);
    try {
      const out = await createPublicLink(doc.id);
      setLink(out);
      onChanged?.();
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke() {
    if (!confirm('¿Revocar el link público? Nadie con el enlace podrá acceder.')) return;
    setBusy(true);
    setError(null);
    try {
      await revokePublicLink(doc.id);
      setLink(null);
      onChanged?.();
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy() {
    if (!link?.public_url) return;
    try {
      await navigator.clipboard.writeText(link.public_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback silencioso
    }
  }

  if (!link) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Genera un enlace que cualquiera con el link podrá usar para ver/descargar este
          documento sin necesidad de cuenta. Puedes revocarlo en cualquier momento.
        </p>
        {error && (
          <div className="rounded border border-destructive/30 bg-destructive/10 px-2 py-1 text-xs text-destructive">
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={handleCreate}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Globe size={14} />
          {busy ? 'Generando…' : 'Generar link público'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded border border-border bg-muted/30 p-3">
        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Link público
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            readOnly
            value={link.public_url || `(token: ${link.public_token})`}
            className="flex-1 truncate rounded border border-input bg-background px-2 py-1.5 text-xs text-foreground"
            onFocus={(e) => e.currentTarget.select()}
          />
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1 rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copiado' : 'Copiar'}
          </button>
        </div>
        {link.expires_at && (
          <div className="mt-2 text-xs text-muted-foreground">
            Caduca: {new Date(link.expires_at).toLocaleString()}
          </div>
        )}
      </div>

      {error && (
        <div className="rounded border border-destructive/30 bg-destructive/10 px-2 py-1 text-xs text-destructive">
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={handleRevoke}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded border border-destructive/30 px-3 py-1.5 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
      >
        <Link2Off size={14} />
        {busy ? 'Revocando…' : 'Revocar link'}
      </button>
    </div>
  );
}
